import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { parseHtml } from "@components/HTMLParser/utils";
import { Notification } from "@components/Notification/Notification";
import { useRemoveMessageMutation } from "@services/messages/mutations";
import { useBannerMessagesQuery } from "@services/messages/queries";
import type { IBannerMessage } from "@services/messages/types";

export const BannerMessages: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: billsBannerMessages } = useBannerMessagesQuery();

  const { mutateAsync: deleteMessage } = useRemoveMessageMutation();

  if (
    !billsBannerMessages?.length ||
    !billsBannerMessages.some((banner) => banner.bannerMessageId > 0)
  ) {
    return null;
  }

  const removeBannerMessage = async (
    bannerMessageId: IBannerMessage["bannerMessageId"],
  ) => {
    await deleteMessage(bannerMessageId).then(() =>
      queryClient.setQueryData<IBannerMessage[]>(["bannerMessages"], (banner) =>
        (banner ?? []).filter((b) => b.bannerMessageId !== bannerMessageId),
      ),
    );
  };

  return (
    <>
      {billsBannerMessages
        .filter((banner) => banner.bannerMessageId > 0)
        .map((banner, index) => (
          <Notification
            description={parseHtml(banner.text)}
            variant="big"
            color="blue"
            key={index}
            button={
              <Button
                onClick={() => removeBannerMessage(banner.bannerMessageId)}
                aria-label={t("bannerMessages.buttons.close")}
              >
                {t("bannerMessages.buttons.close")}
              </Button>
            }
          />
        ))}
    </>
  );
};
